/**
 * ESputnik tracking composable
 * Предоставляет методы для отправки событий в ESputnik
 */

// Интерфейсы для типов данных ESputnik
interface ProductData {
    productKey: string;
    price?: string | number;
    isInStock?: number;
    quantity?: string | number;
    currency?: string;
    discount?: number;
    [key: string]: any; // Для дополнительных полей (tags)
}

interface CustomerData {
    externalCustomerId?: string;
    user_email?: string;
    user_name?: string;
    user_phone?: string;
    user_city?: string;
    user_card_id?: string;
    user_client_id?: string;
    user_tags_gender?: string;
    [key: string]: any;
}

interface GeneralInfo {
    externalCustomerId?: string;
    user_email?: string;
    user_phone?: string;
    user_es_contact_id?: number;
    s_location?: string;
}

export const useESputnik = () => {
    // Безопасное получение $esputnik только на клиенте
    const nuxtApp = useNuxtApp();
    const $esputnik = import.meta.client ? nuxtApp.$esputnik : null;

    // Генератор GUID для событий корзины и покупок
    const generateGUID = (): string => {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };

    // Инициализация ESputnik
    const init = async () => {
        if (import.meta.client && $esputnik) {
            await $esputnik.init();
        }
    };

    // Событие просмотра страницы товара
    const trackProductPage = (product: ProductData, generalInfo?: GeneralInfo) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                // ESputnik готов, отправляем событие
                const eventData: any = {
                    ProductPage: {
                        productKey: product.productKey,
                        price: String(product.price || ""),
                        isInStock: product.isInStock || 1,
                        ...Object.fromEntries(Object.entries(product).filter(([key]) => key.startsWith("tag_") || !["productKey", "price", "isInStock"].includes(key))),
                    },
                };

                if (generalInfo) {
                    eventData.GeneralInfo = generalInfo;
                }

                console.log("[ESputnik] Sending ProductPage:", eventData);
                $esputnik.sendEvent("ProductPage", eventData);
            } else {
                // ESputnik не готов, ждем и повторяем
                console.log("[ESputnik] Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500; // 500ms

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] Ready after ${attempts} attempts, sending ProductPage`);
                        trackProductPage(product, generalInfo);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие изменения состояния корзины
    const trackStatusCart = (products: ProductData[], guid?: string, additionalTags?: Record<string, any>) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                const cartGuid = guid || generateGUID();

                if (additionalTags) {
                    // Используем расширенную версию для дополнительных параметров
                    const eventData = {
                        StatusCart: {
                            GUID: cartGuid,
                            Products: products.map((product) => ({
                                productKey: product.productKey,
                                price: String(product.price || ""),
                                quantity: String(product.quantity || "1"),
                                currency: product.currency || "UAH",
                                ...Object.fromEntries(Object.entries(product).filter(([key]) => key.startsWith("tag_") && !["productKey", "price", "quantity", "currency"].includes(key))),
                            })),
                            Tags: additionalTags,
                        },
                    };
                    $esputnik.sendEventWithTags("StatusCart", eventData);
                } else {
                    // Простая версия без дополнительных тегов
                    const cartProducts = products.map((product) => ({
                        productKey: product.productKey,
                        price: String(product.price || ""),
                        quantity: String(product.quantity || "1"),
                        currency: product.currency || "UAH",
                        ...Object.fromEntries(Object.entries(product).filter(([key]) => key.startsWith("tag_") && !["productKey", "price", "quantity", "currency"].includes(key))),
                    }));

                    $esputnik.sendEvent("StatusCart", {
                        StatusCart: cartProducts,
                        GUID: cartGuid,
                    });
                }

                return cartGuid;
            } else {
                console.log("[ESputnik] StatusCart - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] StatusCart - Ready after ${attempts} attempts`);
                        return trackStatusCart(products, guid, additionalTags);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] StatusCart - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
                return guid || generateGUID();
            }
        }
        return "";
    };

    // Событие покупки
    const trackPurchasedItems = (orderNumber: string, products: ProductData[], guid?: string, additionalTags?: Record<string, any>) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                if (additionalTags) {
                    // Расширенная версия с дополнительными тегами
                    const eventData = {
                        PurchasedItems: {
                            OrderNumber: orderNumber,
                            ...(guid && { GUID: guid }),
                            Products: products.map((product) => ({
                                product_id: product.productKey,
                                unit_price: String(product.price || ""),
                                quantity: Number(product.quantity || 1),
                            })),
                            Tags: additionalTags,
                        },
                    };
                    $esputnik.sendEventWithTags("PurchasedItems", eventData);
                } else {
                    // Простая версия
                    const eventData: any = {
                        OrderNumber: orderNumber,
                        PurchasedItems: products.map((product) => ({
                            productKey: product.productKey,
                            price: String(product.price || ""),
                            quantity: String(product.quantity || "1"),
                            currency: product.currency || "UAH",
                        })),
                    };

                    if (guid) {
                        eventData.GUID = guid;
                    }

                    $esputnik.sendEvent("PurchasedItems", eventData);
                }
            } else {
                console.log("[ESputnik] PurchasedItems - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] PurchasedItems - Ready after ${attempts} attempts`);
                        trackPurchasedItems(orderNumber, products, guid, additionalTags);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] PurchasedItems - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие данных клиента
    const trackCustomerData = (customerData: CustomerData, generalInfo?: GeneralInfo) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                // ESputnik готов, отправляем событие
                console.log("[ESputnik] Raw CustomerData before sending:", customerData);

                const eventData = {
                    CustomerData: customerData,
                    ...(generalInfo && { GeneralInfo: generalInfo }),
                };

                console.log("[ESputnik] Final CustomerData payload:", eventData);
                $esputnik.sendEvent("CustomerData", eventData);
            } else {
                // ESputnik не готов, ждем и повторяем
                console.log("[ESputnik] CustomerData - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] CustomerData - Ready after ${attempts} attempts`);
                        trackCustomerData(customerData, generalInfo);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] CustomerData - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие страницы категории
    const trackCategoryPage = (categoryKey: string, additionalTags?: Record<string, any>) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                console.log("[ESputnik] Sending CategoryPage with categoryKey:", categoryKey);

                if (additionalTags) {
                    $esputnik.sendEventWithTags("CategoryPage", {
                        CategoryPage: {
                            Category: {
                                categoryKey,
                            },
                            Tags: additionalTags,
                        },
                    });
                } else {
                    $esputnik.sendEvent("CategoryPage", {
                        CategoryPage: {
                            categoryKey,
                        },
                    });
                }
            } else {
                console.log("[ESputnik] CategoryPage - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] CategoryPage - Ready after ${attempts} attempts`);
                        trackCategoryPage(categoryKey, additionalTags);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] CategoryPage - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие поискового запроса без результатов
    const trackSearchRequest = (searchQuery: string, isFound: number = 0) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                $esputnik.sendEvent("SearchRequest", {
                    SearchRequest: {
                        search: searchQuery,
                        isFound,
                    },
                });
            } else {
                console.log("[ESputnik] SearchRequest - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] SearchRequest - Ready after ${attempts} attempts`);
                        trackSearchRequest(searchQuery, isFound);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] SearchRequest - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие добавления в список желаний
    const trackAddToWishlist = (product: ProductData) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                $esputnik.sendEvent("AddToWishlist", {
                    AddToWishlist: {
                        productKey: product.productKey,
                        price: String(product.price || ""),
                        isInStock: product.isInStock || 1,
                    },
                });
            } else {
                console.log("[ESputnik] AddToWishlist - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] AddToWishlist - Ready after ${attempts} attempts`);
                        trackAddToWishlist(product);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] AddToWishlist - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие главной страницы (для рекомендаций)
    const trackMainPage = () => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                $esputnik.sendEvent("MainPage", {});
            } else {
                console.log("[ESputnik] MainPage - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] MainPage - Ready after ${attempts} attempts`);
                        trackMainPage();
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] MainPage - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие страницы 404 (для рекомендаций)
    const trackNotFound = () => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                $esputnik.sendEvent("NotFound", {});
            } else {
                console.log("[ESputnik] NotFound - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] NotFound - Ready after ${attempts} attempts`);
                        trackNotFound();
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] NotFound - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    // Событие показов рекомендаций
    const trackProductImpressions = (impressions: Array<{ product_id: string; container_type: string }>) => {
        if (import.meta.client) {
            if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                $esputnik.sendEvent("ProductImpressions", {
                    ProductImpression: impressions,
                });
            } else {
                console.log("[ESputnik] ProductImpressions - Not ready, waiting for initialization...");
                let attempts = 0;
                const maxAttempts = 10;
                const retryInterval = 500;

                const retry = () => {
                    attempts++;
                    if ($esputnik && $esputnik.isReady && $esputnik.isReady()) {
                        console.log(`[ESputnik] ProductImpressions - Ready after ${attempts} attempts`);
                        trackProductImpressions(impressions);
                    } else if (attempts < maxAttempts) {
                        setTimeout(retry, retryInterval);
                    } else {
                        console.warn("[ESputnik] ProductImpressions - Failed to initialize after maximum attempts");
                    }
                };

                setTimeout(retry, retryInterval);
            }
        }
    };

    return {
        // Методы инициализации
        init,
        generateGUID,

        // Основные события
        trackProductPage,
        trackStatusCart,
        trackPurchasedItems,
        trackCustomerData,
        trackCategoryPage,
        trackSearchRequest,
        trackAddToWishlist,

        // Дополнительные события
        trackMainPage,
        trackNotFound,
        trackProductImpressions,

        // Прямой доступ к ESputnik API (только на клиенте)
        sendEvent: $esputnik ? $esputnik.sendEvent.bind($esputnik) : () => {},
        sendEventWithTags: $esputnik ? $esputnik.sendEventWithTags.bind($esputnik) : () => {},
        isReady: $esputnik ? $esputnik.isReady.bind($esputnik) : () => false,
    };
};

// Экспорт типов для использования в других файлах
export type { ProductData, CustomerData, GeneralInfo };
